import React, { useMemo, useState } from 'react'
import {
  InputWithUnit,
  INPUT_WITH_UNIT_DEMO_UNITS,
  INPUT_WITH_UNIT_PREVIEW_STATE_LABELS,
  INPUT_WITH_UNIT_PREVIEW_STATES,
  type InputWithUnitPreviewState,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './InputWithUnitShowcase.css'

const InputWithUnitShowcase: React.FC = () => {
  const [value, setValue] = useState('250')
  const [unit, setUnit] = useState('mg')
  const [label, setLabel] = useState('Quantity')
  const [helperText, setHelperText] = useState('')
  const [disabled, setDisabled] = useState(false)
  const [optional, setOptional] = useState(false)
  const [error, setError] = useState(false)
  const [highlightedState, setHighlightedState] =
    useState<InputWithUnitPreviewState>('default')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'input-with-unit-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="input-with-unit-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as InputWithUnitPreviewState,
                  )
                }
              >
                {INPUT_WITH_UNIT_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {INPUT_WITH_UNIT_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'input-with-unit-label',
            label: 'Label',
            control: (
              <input
                id="input-with-unit-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
          {
            id: 'input-with-unit-helper',
            label: 'Helper text',
            control: (
              <input
                id="input-with-unit-helper"
                className="ui-field-control"
                type="text"
                value={helperText}
                placeholder="Optional helper text"
                onChange={(event) => setHelperText(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'input-with-unit-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="input-with-unit-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'input-with-unit-optional',
            label: 'Optional',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="input-with-unit-optional"
                  type="checkbox"
                  checked={optional}
                  onChange={(event) => setOptional(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'input-with-unit-error',
            label: 'Error',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="input-with-unit-error"
                  type="checkbox"
                  checked={error}
                  onChange={(event) => setError(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [disabled, error, helperText, highlightedState, label, optional],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Input with unit"
        description="Numeric or text input with a unit selector dropdown, matching Input field styling."
        stageClassName="ui-preview__stage--wide input-with-unit-showcase__stage-wrap"
      >
        <div className={`input-with-unit-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Enter a value and pick a unit from the dropdown. Property controls
              apply to the first example.
            </p>
            <div className="input-with-unit-showcase__live">
              <InputWithUnit
                label={label}
                value={value}
                unit={unit}
                units={INPUT_WITH_UNIT_DEMO_UNITS}
                disabled={disabled}
                optional={optional}
                error={error}
                helperText={helperText || undefined}
                onChange={setValue}
                onUnitChange={setUnit}
              />
              <InputWithUnit
                label="Amount"
                defaultValue="1,000.00"
                defaultUnit="USD"
                units={[
                  { value: 'USD', label: 'USD' },
                  { value: 'EUR', label: 'EUR' },
                  { value: 'GBP', label: 'GBP' },
                ]}
              />
            </div>
          </section>

          <section className="input-with-unit-showcase__matrix">
            <h3 className="ui-showcase-live__title">States</h3>
            {INPUT_WITH_UNIT_PREVIEW_STATES.map((state) => (
              <div
                key={state}
                className={`input-with-unit-showcase__row${
                  highlightedState === state
                    ? ' input-with-unit-showcase__row--selected'
                    : ''
                }${
                  state === 'unit-open'
                    ? ' input-with-unit-showcase__row--expanded'
                    : ''
                }`}
              >
                <h4 className="input-with-unit-showcase__label">
                  {INPUT_WITH_UNIT_PREVIEW_STATE_LABELS[state]}
                </h4>
                <InputWithUnit previewState={state} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Input with unit"
        sections={propertySections}
      />
    </>
  )
}

export default InputWithUnitShowcase
